import React, { useState } from "react";
import { IconDownload } from "@tabler/icons-react";
import ColumnsFilter from "../../components/common/ColumnsFilter";
import DropdownFilter from "../../components/common/DropdownFilter";
import NormalSearch from "../../components/common/NormalSearch";
import ListView from "../../components/common/ListView";
import CommonModal from "../../components/common/CommonModal";
import { useFuelListData } from "../../apis/fuelApis";
import formatDate, { maskedSecretKey } from "../../utilities/helper";

interface FuelRecord {
  driver_id: string;
  vehicle_id: string;
  fuel_date: string;
  liters: number;
  price: number;
  fuelType: string;
  address: string;
  fuel_slip: string;
}

const Fuel: React.FC = () => {
  // State management
  const [openFuelSlip, setOpenFuelSlip] = useState<boolean>(false);
  const [openImage, setOpenImage] = useState<string>("");

  // API data
  const { data, isLoading, error } = useFuelListData(1, 10, "");

  // Modal handlers
  const toggleFuelSlipModal = (image: string = "") => {
    setOpenFuelSlip(!openFuelSlip);
    setOpenImage(image);
  };

  const handleDownloadImage = async () => {
    try {
      const response = await fetch(openImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "fuel-slip.jpg";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
    toggleFuelSlipModal();
  };

  // Table columns configuration
  const columns = [
    {
      label: "#",
      render: (_: FuelRecord, index: number) => index + 1,
    },
    {
      label: "Driver Id",
      render: (record: FuelRecord) => maskedSecretKey(record.driver_id),
    },
    {
      label: "Vehicle Id",
      render: (record: FuelRecord) => maskedSecretKey(record.vehicle_id),
    },
    {
      label: "Fuel Date",
      render: (record: FuelRecord) => formatDate(record.fuel_date),
    },
    {
      label: "Liters",
      render: (record: FuelRecord) => record.liters,
    },
    {
      label: "Cost",
      render: (record: FuelRecord) => record.price,
    },
    {
      label: "Fuel Type",
      render: (record: FuelRecord) => record.fuelType,
    },

    {
      label: "Attachment",
      render: (record: FuelRecord) => (
        <div
          onClick={() => toggleFuelSlipModal(record.fuel_slip)}
          className="flex items-center gap-2 truncate max-w-xs"
        >
          <img
            className="w-24 h-24"
            src={record.fuel_slip}
            alt={`Fuel slip for ${record.driver_id}`}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-4 space-y-4">
        <h1 className="text-3xl text-[#212121]">Driver Management</h1>

        <div className="bg-[#F7F6F2] rounded-3xl">
          {/* Header Section */}
          <div className="flex justify-between items-center w-full p-4">
            <div>
              <h1 className="text-3xl text-[#212121]">Fuel History</h1>
            </div>

            {/* Filter and Export Controls */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <ColumnsFilter columnsData={[]} />
                <DropdownFilter filterheadings={[]} />
                <NormalSearch />
              </div>
              <button className="p-2 text-sm rounded-full border border-[#D8D8D8] bg-white">
                <IconDownload stroke={1.5}  size={20}/>
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div>
            <ListView
              data={data?.fuel ?? []}
              onRowClick={(client: FuelRecord) =>
                console.log("Clicked:", client)
              }
              columns={columns}
            />
          </div>
        </div>
      </div>

      {/* Fuel Slip Modal */}
      <CommonModal
        title="Fuel Slip"
        open={openFuelSlip}
        closeButtonText="Cancel"
        onClose={() => toggleFuelSlipModal()}
        actionButtonText="Download"
        actionButtonOnClick={handleDownloadImage}
        actionButtonIcon={<IconDownload size={18} />}
      >
        <div className="p-6">
          <img src={openImage} alt="Fuel slip" className="w-fit rounded" />
        </div>
      </CommonModal>
    </>
  );
};

export default Fuel;
